import {
  Badge, Chip,
  Typography
} from '@material-ui/core';
import { makeStyles, withStyles, Theme } from '@material-ui/core/styles';
import {
  InfiniteScrollGrid
} from '@roc/feature-app-core';
import { PORTAL_COLORS, formatDate } from '@roc/feature-utils';
import {
  ConfirmDialog,
  DropdownMenu,
  InformationDialog,
  LinkColumn, SelectFilterComponent,
  SelectFloatingFilterComponent, SelectMode, StatusChip,
  StatusType
} from '@roc/ui';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { Check, Person, Redo } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useInternalValuationStore } from '../hooks/useInternalValuationStore';
import { InternalValuationDashboardToolbar } from './internalValuationDashboardToolbar';
import { DocumentName, useDocumentStore } from '@roc/feature-documents';
import { Document as RocDocument } from '@roc/feature-types';
import { areActionsUnavailableBasedOnTaskQueueStatus, AssignToDialog, TASK_IN_PROGRESS_MESSAGE, TaskStatus } from '@roc/feature-general-tasks';

export const StyledBadge = withStyles(theme => ({
  badge: {
    right: -12,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    fontSize: '11px',
    backgroundColor: PORTAL_COLORS.unreadMessages,
    color: 'white',
    fontWeight: 700,
    userSelect: 'none',
  },
}))(Badge);

const useStyles = makeStyles((theme: Theme) => ({
  informationDialogText: {
    margin: '-32px',
  },
}));

export const InternalValuationLoansGrid = observer(({ columns }) => {
  const classes = useStyles();
  const { internalValuationStore } = useInternalValuationStore();
  const [confirmModalLoan, setConfirmModalLoan] = useState<any>(null);
  const [assingToModalLoan, setAssignToModalLoan] = useState<any>(null);
  const [informationModalText, setInformationModalText] = useState('');
  const { documentStore } = useDocumentStore();

  useEffect(() => {
    internalValuationStore.fetchValuationReviewers();
  }, []);

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    loanIdCellRenderer: params => {
      const url = '/nportal/#/loan_information/' + params.value;
      return (
        <LinkColumn
          value={params.value}
          url={'#'}
          onClick={e => {
            open(url);
            e.preventDefault();
          }}
        />
      )
    },
    actionsCellRenderer: (params) => <DropdownMenu options={[{
      icon: Check,
      name: 'Mark as Completed',
      action: () => handleOpenConfirmModal(params.node.data),
    }, {
      icon: Redo,
      name: 'Assign To',
      action: () => handleOpenAssignToModal(params.node.data),
    }]}>
      <MoreHorizIcon />
    </DropdownMenu>,
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.LOAN} />
      ) : null,
    subStatusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.SUB_STATUS} />
      ) : null,
    assigneeCellRenderer(params) {
      return (
        <>
          {params.value && (
            <Chip
              style={{ maxWidth: '100%', textOverflow: 'ellipsis' }}
              icon={<Person />}
              variant="outlined"
              size="small"
              label={params.value}
            />
          )}
        </>
      );
    },
    timeElapsedCellRenderer(params) {
      const row = params.node.data;
      const timeElapsed: number = row?.timeElapsed;
      if (timeElapsed) {
        const days = Math.trunc(timeElapsed / 86400);
        const hours = Math.trunc((timeElapsed % 86400) / 3600);
        return (days > 0 ? `${days}D ` : '') + `${hours}H`;
      } else {
        return null;
      }
    },
    priorityCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.PRIORITY}
            label={params.value}
            size="small"
          />
        )
      );
    },
  };

  const handleOpenConfirmModal = (data: any) => {
    if (areActionsUnavailableBasedOnTaskQueueStatus(data.taskQueueStatus)) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else {
      setConfirmModalLoan(data);
    }
  }

  const handleOpenAssignToModal = (data: any) => {
    if (areActionsUnavailableBasedOnTaskQueueStatus(data.taskQueueStatus)) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else {
      setAssignToModalLoan(data);
    }
  }

  const handleSubmitMarkAsComplete = () => {
    if (confirmModalLoan !== null) {
      documentStore.loanTodosStore.updateTodoStatusV2(
        { taskId: confirmModalLoan.taskId } as unknown as RocDocument,
        TaskStatus.ACCEPTED,
        () => {
          setConfirmModalLoan(null);
          internalValuationStore.internalValuationLoansGridStore.fetchGridData();
        }
      );
    }
  }

  const onRowSelected = params => {
    const isSelected = internalValuationStore.setSelectedRecords(
      params?.data,
      params?.node.selected
    );
    params.node.setSelected(isSelected);
  }

  return (
    <>
      <InfiniteScrollGrid
        columns={columns}
        frameworkComponents={frameworkComponents}
        selectMode={SelectMode.MULTIPLE}
        headerCheckboxSelection={true}
        suppressRowClickSelection={true}
        onRowSelected={onRowSelected}
        toolbar={
          <InternalValuationDashboardToolbar
            internalValuationStore={internalValuationStore}
          />
        }
        store={internalValuationStore.internalValuationLoansGridStore}
      />
      <ConfirmDialog
        handleConfirm={handleSubmitMarkAsComplete}
        handleClose={() => setConfirmModalLoan(null)}
        handleCancel={() => setConfirmModalLoan(null)}
        open={confirmModalLoan !== null}
        body={<Typography>Please confirm you want to mark the task as completed.</Typography>}
        confirmButtonText="Confirm"
        cancelButtonText="Close"
        title={'Confirmation'}
      />
      <AssignToDialog
        open={assingToModalLoan !== null}
        loanId={assingToModalLoan?.loanId!}
        taskId={assingToModalLoan?.taskId!}
        primaryReason={assingToModalLoan?.reason}
        readOnly
        taskName={DocumentName.INTERNAL_VALUATION_REPORT}
        showExtraFields={false}
        hasComments={false}
        handleClose={() => {
          setAssignToModalLoan(null);
          internalValuationStore.internalValuationLoansGridStore.fetchGridData();
        }}
      />
      <InformationDialog
        open={informationModalText !== ''}
        title="Action Unavailable"
        handleClose={() => setInformationModalText('')}
        body={<Typography className={classes.informationDialogText}>{informationModalText}</Typography>}
      />
    </>
  );
});
